import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ButtonWithHoverEffect from './component/ButtonWithHoverEffect';
import ScrollIndicator from './component/ScrollIndicator';

gsap.registerPlugin(TextPlugin, ScrollTrigger);

const EnhancedHeroSection = () => {
  const [showScroll, setShowScroll] = useState(false);

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const roleRef = useRef(null);
  const descRef = useRef(null);
  const socialRef = useRef(null);
  const overlayRef = useRef(null);
  
  const startNameAnimation = () => {
    // Kill any existing animations first
    gsap.killTweensOf([".outline-1", ".fill-1"]);
    
    // Reset states
    const outlines = document.querySelectorAll('.outline-1');
    const fills = document.querySelectorAll('.fill-1');
    
    // Set initial states
    gsap.set(outlines, {
      strokeDasharray: function(index, element) {
        const length = element.getTotalLength();
        return `${length} ${length}`;
      },
      strokeDashoffset: function(index, element) {
        return element.getTotalLength();
      }
    });
    
    gsap.set(fills, { opacity: 0 });
    
    // Create timeline for name animation
    const nameTl = gsap.timeline();
    
    // Animate outlines
    nameTl.to(outlines, {
      duration: 1.5,
      strokeDashoffset: 0,
      ease: "power3.inOut",
      stagger: {
        each: 0.1,
        from: "start"
      }
    });

    // Animate fills
    nameTl.to(fills, {
      duration: 0.8,
      opacity: 1,
      stagger: {
        each: 0.1,
        from: "start"
      }
    }, "-=1");

    return nameTl;
  };

  useEffect(() => {
    // 延迟显示滚动指示器
    const timer = setTimeout(() => {
      setShowScroll(true);
    }, 4000); // 在主要动画完成后显示

    // Initialize stroke lengths
    const outlines = document.querySelectorAll('.outline-1');
    outlines.forEach(outline => {
      outline.style.strokeDasharray = `${outline.getTotalLength()} ${outline.getTotalLength()}`;
      outline.style.strokeDashoffset = outline.getTotalLength();
    });

    // Create main timeline
    const mainTl = gsap.timeline({
      defaults: { ease: 'power3.out' }
    });

    // Initial setup - hide elements
    gsap.set([svgRef.current, roleRef.current, descRef.current, socialRef.current], {
      y: 50,
      opacity: 0
    });

    // Overlay animation
    mainTl.to(overlayRef.current, {
      height: "0%",
      duration: 1.2,
      ease: "power4.inOut"
    });

    // Name animation
    mainTl.add(startNameAnimation(), "-=0.5");

    // SVG container fade in
    mainTl.to(svgRef.current, {
      y: 0,
      opacity: 1,
      duration: 0.1
    }, "-=2");

    // Role animation
    mainTl.to(roleRef.current, {
      y: 0,
      opacity: 1,
      duration: 0.8
    }, "-=0.6");

    // Description animation
    mainTl.to(descRef.current, {
      y: 0,
      opacity: 1,
      duration: 0.8
    }, "-=0.4");

    // Social links animation
    mainTl.to(socialRef.current, {
      y: 0,
      opacity: 1,
      duration: 0.8
    }, "-=0.4");

    return () => {
      mainTl.kill();
      clearTimeout(timer);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-sky-50 overflow-hidden">
      {/* Overlay */}
      <div 
        ref={overlayRef} 
        className="absolute inset-0 bg-white z-10"
      />
      
      {/* Background Grid */}
      <div className="absolute inset-0 z-0" 
        style={{
          backgroundImage: 'linear-gradient(to right, #f1f5f9 1px, transparent 1px), linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)',
          backgroundSize: '4rem 4rem'
        }}
      />
      
      {/* Main Content */}
      <div className="relative z-20 text-center px-4">
        {/* Animated Name */}
        <div ref={svgRef} className="mb-4 flex justify-center w-full max-w-[500px] mx-auto px-4">
          <svg className="w-full h-auto" viewBox="0 0 73 14" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="gradientY" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0ea5e9" />  
                <stop offset="100%" stopColor="#1e9de5" />
              </linearGradient>
              <linearGradient id="gradientU" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1e9de5" />
                <stop offset="100%" stopColor="#2e95e1" />
              </linearGradient>
              <linearGradient id="gradientW" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2e95e1" />
                <stop offset="100%" stopColor="#3e8ddd" />
              </linearGradient>
              <linearGradient id="gradientE1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3e8ddd" />
                <stop offset="100%" stopColor="#4e85d9" />
              </linearGradient>
              <linearGradient id="gradientI1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#4e85d9" />
                <stop offset="100%" stopColor="#5e7dd5" />
              </linearGradient>
              {/* Space for Xie */}
              <linearGradient id="gradientX" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#5e7dd5" />
                <stop offset="100%" stopColor="#6275d1" />
              </linearGradient>
              <linearGradient id="gradientI2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6275d1" />
                <stop offset="100%" stopColor="#636ecd" />
              </linearGradient>
              <linearGradient id="gradientE2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#636ecd" />
                <stop offset="100%" stopColor="#64748b" />
              </linearGradient>
            </defs>
            <g id="text">
              <g id="letter-y">
                <path className="outline-1" d="M0.409088 1.36365H2.02272L5.25 6.79547H5.38636L8.61363 1.36365H10.2273L6.02272 8.20456V13H4.61363V8.20456L0.409088 1.36365Z" stroke="url(#gradientY)" strokeWidth="0.4"/>
                <path className="fill-1" d="M0.409088 1.36365H2.02272L5.25 6.79547H5.38636L8.61363 1.36365H10.2273L6.02272 8.20456V13H4.61363V8.20456L0.409088 1.36365Z" fill="url(#gradientY)"/>
              </g>
              
              <g id="letter-u">
                <path className="outline-1" d="M16.7273 9.4318V4.27271H18.0682V13H16.7273V11.5227H16.6364C16.4318 11.9659 16.1136 12.3428 15.6818 12.6534C15.25 12.9602 14.7045 13.1136 14.0454 13.1136C13.5 13.1136 13.0151 12.9943 12.5909 12.7557C12.1667 12.5132 11.8333 12.1496 11.5909 11.6648C11.3485 11.1761 11.2273 10.5606 11.2273 9.81816V4.27271H12.5682V9.72725C12.5682 10.3636 12.7462 10.8712 13.1023 11.25C13.4621 11.6288 13.9204 11.8182 14.4773 11.8182C14.8106 11.8182 15.1496 11.7329 15.4943 11.5625C15.8428 11.392 16.1345 11.1307 16.3693 10.7784C16.6079 10.4261 16.7273 9.97725 16.7273 9.4318Z" stroke="url(#gradientU)" strokeWidth="0.4"/>
                <path className="fill-1" d="M16.7273 9.4318V4.27271H18.0682V13H16.7273V11.5227H16.6364C16.4318 11.9659 16.1136 12.3428 15.6818 12.6534C15.25 12.9602 14.7045 13.1136 14.0454 13.1136C13.5 13.1136 13.0151 12.9943 12.5909 12.7557C12.1667 12.5132 11.8333 12.1496 11.5909 11.6648C11.3485 11.1761 11.2273 10.5606 11.2273 9.81816V4.27271H12.5682V9.72725C12.5682 10.3636 12.7462 10.8712 13.1023 11.25C13.4621 11.6288 13.9204 11.8182 14.4773 11.8182C14.8106 11.8182 15.1496 11.7329 15.4943 11.5625C15.8428 11.392 16.1345 11.1307 16.3693 10.7784C16.6079 10.4261 16.7273 9.97725 16.7273 9.4318Z" fill="url(#gradientU)"/>
              </g>
              
              <g id="letter-w">
                <path className="outline-1" d="M22.5014 13L19.8423 4.27271H21.2514L23.1378 10.9545H23.2287L25.0923 4.27271H26.5241L28.365 10.9318H28.456L30.3423 4.27271H31.7514L29.0923 13H27.7741L25.865 6.29543H25.7287L23.8196 13H22.5014Z" stroke="url(#gradientW)" strokeWidth="0.4"/>
                <path className="fill-1" d="M22.5014 13L19.8423 4.27271H21.2514L23.1378 10.9545H23.2287L25.0923 4.27271H26.5241L28.365 10.9318H28.456L30.3423 4.27271H31.7514L29.0923 13H27.7741L25.865 6.29543H25.7287L23.8196 13H22.5014Z" fill="url(#gradientW)"/>
              </g>

              <g id="letter-e">
                <path className="outline-1" d="M36.8864 13.1818C36.0455 13.1818 35.3201 12.9962 34.7102 12.625C34.1042 12.25 33.6364 11.7272 33.3068 11.0568C32.9811 10.3825 32.8182 9.59845 32.8182 8.70451C32.8182 7.81057 32.9811 7.02269 33.3068 6.34088C33.6364 5.65527 34.0947 5.12118 34.6818 4.7386C35.2727 4.35224 35.9621 4.15906 36.75 4.15906C37.2045 4.15906 37.6534 4.23481 38.0966 4.38633C38.5398 4.53784 38.9432 4.78406 39.3068 5.12497C39.6705 5.46209 39.9602 5.90906 40.1761 6.46588C40.392 7.02269 40.5 7.7083 40.5 8.52269V9.09088H33.7727V7.93178H39.1364C39.1364 7.43936 39.0379 6.99997 38.8409 6.6136C38.6477 6.22724 38.3712 5.92231 38.0114 5.69883C37.6553 5.47535 37.2348 5.3636 36.75 5.3636C36.2159 5.3636 35.7538 5.49618 35.3636 5.76133C34.9773 6.02269 34.6799 6.3636 34.4716 6.78406C34.2633 7.20451 34.1591 7.65527 34.1591 8.13633V8.90906C34.1591 9.56815 34.2727 10.1269 34.5 10.5852C34.7311 11.0397 35.0511 11.3863 35.4602 11.625C35.8693 11.8598 36.3447 11.9772 36.8864 11.9772C37.2386 11.9772 37.5568 11.928 37.8409 11.8295C38.1288 11.7272 38.3769 11.5757 38.5852 11.375C38.7936 11.1704 38.9545 10.9166 39.0682 10.6136L40.3636 10.9772C40.2273 11.4166 39.9981 11.803 39.6761 12.1363C39.3542 12.4659 38.9564 12.7235 38.483 12.9091C38.0095 13.0909 37.4773 13.1818 36.8864 13.1818Z" stroke="url(#gradientE1)" strokeWidth="0.4"/>
                <path className="fill-1" d="M36.8864 13.1818C36.0455 13.1818 35.3201 12.9962 34.7102 12.625C34.1042 12.25 33.6364 11.7272 33.3068 11.0568C32.9811 10.3825 32.8182 9.59845 32.8182 8.70451C32.8182 7.81057 32.9811 7.02269 33.3068 6.34088C33.6364 5.65527 34.0947 5.12118 34.6818 4.7386C35.2727 4.35224 35.9621 4.15906 36.75 4.15906C37.2045 4.15906 37.6534 4.23481 38.0966 4.38633C38.5398 4.53784 38.9432 4.78406 39.3068 5.12497C39.6705 5.46209 39.9602 5.90906 40.1761 6.46588C40.392 7.02269 40.5 7.7083 40.5 8.52269V9.09088H33.7727V7.93178H39.1364C39.1364 7.43936 39.0379 6.99997 38.8409 6.6136C38.6477 6.22724 38.3712 5.92231 38.0114 5.69883C37.6553 5.47535 37.2348 5.3636 36.75 5.3636C36.2159 5.3636 35.7538 5.49618 35.3636 5.76133C34.9773 6.02269 34.6799 6.3636 34.4716 6.78406C34.2633 7.20451 34.1591 7.65527 34.1591 8.13633V8.90906C34.1591 9.56815 34.2727 10.1269 34.5 10.5852C34.7311 11.0397 35.0511 11.3863 35.4602 11.625C35.8693 11.8598 36.3447 11.9772 36.8864 11.9772C37.2386 11.9772 37.5568 11.928 37.8409 11.8295C38.1288 11.7272 38.3769 11.5757 38.5852 11.375C38.7936 11.1704 38.9545 10.9166 39.0682 10.6136L40.3636 10.9772C40.2273 11.4166 39.9981 11.803 39.6761 12.1363C39.3542 12.4659 38.9564 12.7235 38.483 12.9091C38.0095 13.0909 37.4773 13.1818 36.8864 13.1818Z" fill="url(#gradientE1)"/>
              </g>

              <g id="letter-i">
                <path class="outline-1" d="M42.5398 13V4.27273H43.8807V13H42.5398ZM43.2216 2.81818C42.9602 2.81818 42.7348 2.72917 42.5455 2.55114C42.3598 2.37311 42.267 2.15909 42.267 1.90909C42.267 1.65909 42.3598 1.44508 42.5455 1.26705C42.7348 1.08902 42.9602 1 43.2216 1C43.483 1 43.7064 1.08902 43.892 1.26705C44.0814 1.44508 44.1761 1.65909 44.1761 1.90909C44.1761 2.15909 44.0814 2.37311 43.892 2.55114C43.7064 2.72917 43.483 2.81818 43.2216 2.81818Z" stroke="url(#gradientI1)" stroke-width="0.4" stroke-dasharray="200" stroke-dashoffset="200"/>
                <path class="fill-1" d="M42.5398 13V4.27273H43.8807V13H42.5398ZM43.2216 2.81818C42.9602 2.81818 42.7348 2.72917 42.5455 2.55114C42.3598 2.37311 42.267 2.15909 42.267 1.90909C42.267 1.65909 42.3598 1.44508 42.5455 1.26705C42.7348 1.08902 42.9602 1 43.2216 1C43.483 1 43.7064 1.08902 43.892 1.26705C44.0814 1.44508 44.1761 1.65909 44.1761 1.90909C44.1761 2.15909 44.0814 2.37311 43.892 2.55114C43.7064 2.72917 43.483 2.81818 43.2216 2.81818Z" fill="url(#gradientI1)"/>
              </g>

              <g id="letter-x">
                <path class="outline-1" d="M51.7003 1.36365L54.7003 6.20456H54.7912L57.7912 1.36365H59.4503L55.7912 7.18183L59.4503 13H57.7912L54.7912 8.25001H54.7003L51.7003 13H50.0412L53.7912 7.18183L50.0412 1.36365H51.7003Z" stroke="url(#gradientX)" stroke-width="0.4" stroke-dasharray="200" stroke-dashoffset="200"/>
                <path class="fill-1" d="M51.7003 1.36365L54.7003 6.20456H54.7912L57.7912 1.36365H59.4503L55.7912 7.18183L59.4503 13H57.7912L54.7912 8.25001H54.7003L51.7003 13H50.0412L53.7912 7.18183L50.0412 1.36365H51.7003Z" fill="url(#gradientX)"/>
              </g>

              <g id="letter-i-2">
                <path class="outline-1" d="M61.1023 13V4.27273H62.4432V13H61.1023ZM61.7841 2.81818C61.5227 2.81818 61.2973 2.72917 61.108 2.55114C60.9223 2.37311 60.8295 2.15909 60.8295 1.90909C60.8295 1.65909 60.9223 1.44508 61.108 1.26705C61.2973 1.08902 61.5227 1 61.7841 1C62.0455 1 62.2689 1.08902 62.4545 1.26705C62.6439 1.44508 62.7386 1.65909 62.7386 1.90909C62.7386 2.15909 62.6439 2.37311 62.4545 2.55114C62.2689 2.72917 62.0455 2.81818 61.7841 2.81818Z" stroke="url(#gradientI2)" stroke-width="0.4" stroke-dasharray="200" stroke-dashoffset="200"/>
                <path class="fill-1" d="M61.1023 13V4.27273H62.4432V13H61.1023ZM61.7841 2.81818C61.5227 2.81818 61.2973 2.72917 61.108 2.55114C60.9223 2.37311 60.8295 2.15909 60.8295 1.90909C60.8295 1.65909 60.9223 1.44508 61.108 1.26705C61.2973 1.08902 61.5227 1 61.7841 1C62.0455 1 62.2689 1.08902 62.4545 1.26705C62.6439 1.44508 62.7386 1.65909 62.7386 1.90909C62.7386 2.15909 62.6439 2.37311 62.4545 2.55114C62.2689 2.72917 62.0455 2.81818 61.7841 2.81818Z" fill="url(#gradientI2)"/>
              </g>

              <g id="letter-e-2">
                <path class="outline-1" d="M68.5582 13.1818C67.7173 13.1818 66.9919 12.9962 66.3821 12.625C65.776 12.25 65.3082 11.7272 64.9787 11.0568C64.6529 10.3825 64.4901 9.59845 64.4901 8.70451C64.4901 7.81057 64.6529 7.02269 64.9787 6.34088C65.3082 5.65527 65.7666 5.12118 66.3537 4.7386C66.9446 4.35224 67.634 4.15906 68.4219 4.15906C68.8764 4.15906 69.3253 4.23481 69.7685 4.38633C70.2116 4.53784 70.615 4.78406 70.9787 5.12497C71.3423 5.46209 71.6321 5.90906 71.848 6.46588C72.0639 7.02269 72.1719 7.7083 72.1719 8.52269V9.09088H65.4446V7.93178H70.8082C70.8082 7.43936 70.7097 6.99997 70.5128 6.6136C70.3196 6.22724 70.0431 5.92231 69.6832 5.69883C69.3272 5.47535 68.9067 5.3636 68.4219 5.3636C67.8878 5.3636 67.4257 5.49618 67.0355 5.76133C66.6491 6.02269 66.3518 6.3636 66.1435 6.78406C65.9351 7.20451 65.831 7.65527 65.831 8.13633V8.90906C65.831 9.56815 65.9446 10.1269 66.1719 10.5852C66.4029 11.0397 66.723 11.3863 67.1321 11.625C67.5412 11.8598 68.0166 11.9772 68.5582 11.9772C68.9105 11.9772 69.2287 11.928 69.5128 11.8295C69.8007 11.7272 70.0488 11.5757 70.2571 11.375C70.4654 11.1704 70.6264 10.9166 70.7401 10.6136L72.0355 10.9772C71.8991 11.4166 71.67 11.803 71.348 12.1363C71.026 12.4659 70.6283 12.7235 70.1548 12.9091C69.6813 13.0909 69.1491 13.1818 68.5582 13.1818Z" stroke="url(#gradientE2)" stroke-width="0.4" stroke-dasharray="200" stroke-dashoffset="200"/>
                <path class="fill-1" d="M68.5582 13.1818C67.7173 13.1818 66.9919 12.9962 66.3821 12.625C65.776 12.25 65.3082 11.7272 64.9787 11.0568C64.6529 10.3825 64.4901 9.59845 64.4901 8.70451C64.4901 7.81057 64.6529 7.02269 64.9787 6.34088C65.3082 5.65527 65.7666 5.12118 66.3537 4.7386C66.9446 4.35224 67.634 4.15906 68.4219 4.15906C68.8764 4.15906 69.3253 4.23481 69.7685 4.38633C70.2116 4.53784 70.615 4.78406 70.9787 5.12497C71.3423 5.46209 71.6321 5.90906 71.848 6.46588C72.0639 7.02269 72.1719 7.7083 72.1719 8.52269V9.09088H65.4446V7.93178H70.8082C70.8082 7.43936 70.7097 6.99997 70.5128 6.6136C70.3196 6.22724 70.0431 5.92231 69.6832 5.69883C69.3272 5.47535 68.9067 5.3636 68.4219 5.3636C67.8878 5.3636 67.4257 5.49618 67.0355 5.76133C66.6491 6.02269 66.3518 6.3636 66.1435 6.78406C65.9351 7.20451 65.831 7.65527 65.831 8.13633V8.90906C65.831 9.56815 65.9446 10.1269 66.1719 10.5852C66.4029 11.0397 66.723 11.3863 67.1321 11.625C67.5412 11.8598 68.0166 11.9772 68.5582 11.9772C68.9105 11.9772 69.2287 11.928 69.5128 11.8295C69.8007 11.7272 70.0488 11.5757 70.2571 11.375C70.4654 11.1704 70.6264 10.9166 70.7401 10.6136L72.0355 10.9772C71.8991 11.4166 71.67 11.803 71.348 12.1363C71.026 12.4659 70.6283 12.7235 70.1548 12.9091C69.6813 13.0909 69.1491 13.1818 68.5582 13.1818Z" fill="url(#gradientE2)"/>
              </g>
            </g>
          </svg>
        </div>
        
        {/* Role */}
        <h2 
          ref={roleRef} 
          className="text-3xl text-slate-700 mb-6 font-light tracking-wide"
        >
          Senior Full-Stack Developer
        </h2>
        
        {/* Description */}
        <div ref={descRef} className="max-w-2xl mx-auto mb-8">
          <p className="text-xl text-slate-600 leading-relaxed mb-2">
            Combining hands-on development (React.js, TypeScript, .NET Core) with strong execution in cross-functional delivery.
          </p>
          <p className="text-xl text-slate-600 leading-relaxed">
            5+ years of experience shipping customer-facing products, integrating APIs, improving performance, and supporting production reliability via on-call and monitoring
          </p>
        </div>
        
        <div ref={socialRef} className="flex justify-center flex-wrap gap-4 sm:space-x-6">
          <ButtonWithHoverEffect 
            href="https://linkedin.com/in/yuwei-xie"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center h-12 px-6 border-2 border-sky-200 text-slate-700 hover:text-white transition-all duration-300 rounded-md"
          >
            <span className="whitespace-nowrap">LinkedIn</span>
          </ButtonWithHoverEffect>
          
          <ButtonWithHoverEffect 
            href="mailto:ivyxie.work@gmail.com"
            className="inline-flex items-center h-12 px-6 border-2 border-sky-200 text-slate-700 hover:text-white transition-all duration-300 rounded-md"
          >
            <span className="whitespace-nowrap">Email</span>
          </ButtonWithHoverEffect>
        </div>
      </div>
      
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-sky-100/40 to-slate-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-gradient-to-br from-slate-100/40 to-sky-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
      
      {/* Code-like Elements */}
      <div className="absolute top-20 left-20 text-slate-200/30 font-mono text-sm">
        &lt;div class="hero"&gt;
      </div>
      <div className="absolute bottom-20 right-20 text-slate-200/30 font-mono text-sm">
        &lt;/div&gt;
      </div>
      <ScrollIndicator 
        className={`transition-opacity duration-500 ${
          showScroll ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
};

export default EnhancedHeroSection;